import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { urls, currentLang } from "./links";

const OG_LOCALE = { ar: "ar_TN", fr: "fr_FR", en: "en_US" };
const DEFAULT_IMAGE = "/og-photo.png";

function absolute(pathOrUrl) {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return urls.absoluteUrl(window.location.origin, pathOrUrl);
}

// Head tags the server renderer injected for the first page. Helmet only
// manages tags it created, so without this a client-side navigation would
// leave the first page's canonical and hreflang links behind (and the
// language switcher reads hreflang).
const SERVER_TAGS = [
  'meta[name="description"]',
  'meta[name="robots"]',
  'link[rel="canonical"]',
  'link[rel="alternate"][hreflang]',
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
  'meta[property^="twitter:"]',
  'script[type="application/ld+json"]',
]
  .map((selector) => `head ${selector}:not([data-react-helmet])`)
  .join(",");
let serverTagsRemoved = false;

function removeServerTags() {
  if (serverTagsRemoved) return;
  serverTagsRemoved = true;
  document.querySelectorAll(SERVER_TAGS).forEach((el) => el.parentNode.removeChild(el));
}

/**
 * Per-page head tags. Mirrors what the API's page renderer puts in the HTML,
 * so the head stays right after React takes over and on client navigation.
 *
 * path:       decoded canonical path of this page (omit for noindex pages)
 * alternates: { ar, fr, en } decoded paths of the page in each language it
 *             exists in (a guide may lack a translation); x-default points
 *             at the Arabic one, else the first present
 */
export default function Seo({ title, description, path, alternates, noindex, jsonLd, image, type = "website" }) {
  const lang = currentLang();
  const canonical = path ? absolute(path) : null;
  const imageUrl = absolute(image || DEFAULT_IMAGE);
  const ld = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  useEffect(removeServerTags, []);

  return (
    <Helmet>
      <html lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} />
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {noindex && <meta name="robots" content="noindex, follow" />}
      {canonical && !noindex && <link rel="canonical" href={canonical} />}
      {alternates &&
        !noindex &&
        urls.LANGS.filter((l) => alternates[l]).map((l) => (
          <link key={l} rel="alternate" hreflang={l} href={absolute(alternates[l])} />
        ))}
      {alternates && !noindex && urls.xDefault(alternates) && (
        <link rel="alternate" hreflang="x-default" href={absolute(urls.xDefault(alternates))} />
      )}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="AvoConsulte" />
      <meta property="og:locale" content={OG_LOCALE[lang]} />
      <meta property="og:image" content={imageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={imageUrl} />
      {ld.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}
